import { useState } from "react";
import { ChevronDown, Star, ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/brand";

/**
 * Premium editorial Tarquínio menu.
 * - Desktop: sticky category rail on left + editorial grid on right
 * - Mobile: accordion with smooth expand/collapse, first category open
 * - "Mais pedido" featured badges
 * - Inline WhatsApp reservation CTA
 */
export const TarqMenu = ({ categories = [] }) => {
  const [active, setActive] = useState(categories[0]?.id);
  const [openId, setOpenId] = useState(categories[0]?.id); // mobile

  const message = "Olá! Gostaria de fazer uma reserva no Tarquínio Beach Club.";
  const current = categories.find((c) => c.id === active) || categories[0];

  return (
    <div data-testid="tarq-menu">
      {/* ============ DESKTOP ============ */}
      <div className="hidden md:grid grid-cols-12 gap-10 lg:gap-16">
        {/* Sidebar rail */}
        <aside className="col-span-4 lg:col-span-3">
          <div className="sticky top-28">
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#E88B4F] font-sans-tarq">
              Categorias
            </span>
            <ul className="mt-6 space-y-1 border-l border-[#0F2A44]/10">
              {categories.map((c) => {
                const isActive = c.id === active;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setActive(c.id)}
                      data-testid={`tarq-menu-tab-${c.id}`}
                      className={`group w-full text-left pl-5 pr-3 py-3 -ml-px border-l-2 transition-all duration-300 ${
                        isActive
                          ? "border-[#E88B4F] text-[#0F2A44]"
                          : "border-transparent text-[#7A6A54] hover:text-[#0F2A44] hover:border-[#0F2A44]/30"
                      }`}
                    >
                      <span
                        className={`block font-serif-tarq text-xl leading-tight transition-transform duration-500 ${isActive ? "italic translate-x-0.5" : "group-hover:translate-x-0.5"}`}
                      >
                        {c.label}
                      </span>
                      <span className="mt-0.5 block text-[10px] tracking-[0.28em] uppercase text-[#7A6A54]/70 font-sans-tarq">
                        {c.items.length} pratos
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Inline WhatsApp CTA */}
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tarq-menu-reserve"
              className="btn btn-md btn-primary-tarq mt-10 w-full"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          </div>
        </aside>

        {/* Content */}
        <section className="col-span-8 lg:col-span-9" key={current?.id}>
          <div className="reveal-up is-visible">
            <span className="text-[10px] tracking-[0.45em] uppercase text-[#E88B4F] font-sans-tarq">
              {current?.label}
            </span>
            <h3 className="mt-3 font-serif-tarq italic text-[#0F2A44] text-4xl lg:text-5xl font-light leading-[1.05] tracking-[-0.02em]">
              {current?.intro || current?.label}
            </h3>
            <div className="mt-6 h-px w-14 bg-[#0F2A44]/30" />
          </div>

          <DishGrid items={current?.items || []} />
        </section>
      </div>

      {/* ============ MOBILE (accordion) ============ */}
      <div className="md:hidden space-y-2" data-testid="tarq-menu-mobile">
        {categories.map((c) => {
          const isOpen = openId === c.id;
          return (
            <div
              key={c.id}
              className="bg-white/60 border border-[#0F2A44]/10 rounded-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : c.id)}
                data-testid={`tarq-accordion-${c.id}`}
                aria-expanded={isOpen}
                aria-controls={`tarq-panel-${c.id}`}
                className="w-full flex items-center justify-between gap-4 px-5 py-5 text-left"
              >
                <div>
                  <span
                    className={`text-[10px] tracking-[0.32em] uppercase ${isOpen ? "text-[#E88B4F]" : "text-[#7A6A54]"} font-sans-tarq transition-colors`}
                  >
                    {c.items.length} pratos
                  </span>
                  <h4
                    className={`mt-1 font-serif-tarq ${isOpen ? "italic text-[#0F2A44]" : "text-[#0F2A44]"} text-2xl leading-tight tracking-tight`}
                  >
                    {c.label}
                  </h4>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-[#0F2A44] shrink-0 transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                  strokeWidth={1.5}
                />
              </button>

              <div
                id={`tarq-panel-${c.id}`}
                role="region"
                aria-hidden={!isOpen}
                className={`grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              >
                <div className="overflow-hidden">
                  <div className="px-5 pb-6">
                    {c.intro && (
                      <p className="font-sans-tarq text-[13px] text-[#7A6A54] leading-relaxed mb-4">
                        {c.intro}
                      </p>
                    )}
                    <DishList items={c.items} compact />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <a
          href={waLink(message)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tarq-menu-reserve-mobile"
          className="btn btn-md btn-primary-tarq mt-6 w-full"
        >
          Reservar via WhatsApp
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
        </a>
      </div>
    </div>
  );
};

/* ---------- Desktop grid ---------- */
const DishGrid = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-0">
    {items.map((it, idx) => (
      <DishRow key={idx} item={it} />
    ))}
  </div>
);

/* ---------- Mobile list ---------- */
const DishList = ({ items, compact }) => (
  <ul className="divide-y divide-[#0F2A44]/10">
    {items.map((it, idx) => (
      <li key={idx}>
        <DishRow item={it} compact={compact} />
      </li>
    ))}
  </ul>
);

const slug = (s) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-");

/* ---------- Single dish row ---------- */
const DishRow = ({ item, compact = false }) => (
  <div
    className={`group flex items-start justify-between gap-5 ${compact ? "py-4" : "py-6 border-b border-[#0F2A44]/10"} transition-colors duration-300 hover:bg-[#F0E8D9]/40 px-2 -mx-2 rounded-sm`}
    data-testid={`tarq-dish-${slug(item.name)}`}
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h5
          className={`font-serif-tarq text-[#0F2A44] ${compact ? "text-lg" : "text-xl lg:text-[1.375rem]"} leading-tight tracking-tight transition-transform duration-500 group-hover:translate-x-0.5`}
        >
          {item.name}
        </h5>
        {item.featured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E88B4F]/15 text-[#D46A2E] text-[9px] tracking-[0.24em] uppercase font-sans-tarq">
            <Star className="h-2.5 w-2.5 fill-[#D46A2E]" strokeWidth={0} />
            Mais pedido
          </span>
        )}
      </div>
      {(item.desc || item.note) && (
        <p className="mt-1.5 font-sans-tarq text-[13px] text-[#7A6A54] leading-relaxed">
          {item.desc || item.note}
        </p>
      )}
    </div>

    {/* Price */}
    <div className="shrink-0 text-right">
      <span
        className={`font-serif-tarq italic text-[#0F2A44] ${compact ? "text-base" : "text-lg lg:text-xl"} tracking-tight`}
      >
        {item.price}
        <span className="text-[#D4734A] ml-0.5">€</span>
      </span>
    </div>
  </div>
);

export default TarqMenu;
